import {
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  useQuery,
} from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

export const useWrappedQuery = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Q = any,
  V extends OperationVariables = OperationVariables,
>(
  query: DocumentNode,
  options?: QueryHookOptions<Q, V>,
) => {
  const [hasResponse, setHasResponse] = useState(false);

  const queryResult = useQuery<Q, V>(query, options);

  useEffect(() => {
    if (queryResult.data && !queryResult.loading) {
      setHasResponse(true);
    }
  }, [queryResult.data, queryResult.loading]);

  return useMemo(() => {
    /*
     * Apollo does not return previous data when refetching. This leads to a bug
     * where `data` becomes an empty object instead of `undefined` when refetching
     * a query that previously errored. Fix this by manually checking for an empty
     * object, and if so, setting `data` to `undefined`. There are no queries or
     * mutations where we fetch nothing off the response.
     *
     * https://github.com/apollographql/apollo-client/pull/6566
     * https://github.com/apollographql/apollo-client/issues/7486
     */
    const queryData = queryResult.data;

    return {
      ...queryResult,
      data:
        queryData &&
        typeof queryData === 'object' &&
        Object.keys(queryData).length === 0
          ? undefined
          : queryData,
      hasResponse,
      setHasResponse,
    };
  }, [hasResponse, queryResult]);
};
